import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import {
  isUserAgentAndroidApp,
} from 'common/util/isUserAgentMobileApp';

import ButtonDeprecated, {
  BUTTON_THEME_BLUE_GREYCLIFF,
} from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrText from 'client/components/SrText';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const DRIVER_SUNLOCK_STATE_INITIAL = 'initial';
const DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET = 'serialNumberSet';
const DRIVER_SUNLOCK_STATE_MANUAL_INPUT = 'manualInput';
const DRIVER_SUNLOCK_STATE_BARCODE_SCANNER = 'barcodeScanner';

const sunlockFormFields = [
  {
    id: 'raspberryPiSerialNumber',
    type: 'short_text',
    properties: {
      placeholder: 'Write serial code here...',
    },
    validations: ['required', 'serialNumber'],
  },
];

const SrManualSunlockForm = ({ onSubmit }) => {
  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({ formFields: sunlockFormFields })}
      validate={createFormValidationFn(sunlockFormFields)}
      render={(formikBag) => (
        <Form>
          {sunlockFormFields.map((formField) => (
            <SrFormikField
              formikBag={formikBag}
              formField={formField}
              theme={SR_FIELD_THEME_ADOBE}
              key={formField.id}
            />
          ))}
          <ButtonDeprecated
            margin='16px 0 0'
            type='submit'
            theme={BUTTON_THEME_BLUE_GREYCLIFF}
          >
            Submit
          </ButtonDeprecated>
        </Form>
      )}
    />
  );
};

SrManualSunlockForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

SrManualSunlockForm.displayName = 'SrManualSunlockForm';

const SrSunSetupDriverJobSunlockBarcode = ({
  userAgent,
  sunlock,
  formikBag,
  onSunlockBarcodeText,
}) => {
  const [viewState, setViewState] = useState(sunlock
    ? DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET
    : DRIVER_SUNLOCK_STATE_INITIAL
  );
  const [sunlockMissingError, setSunlockMissingError] = useState(false);

  const _onSunlock = (sunlock) => {
    setSunlockMissingError(!sunlock);
    setViewState(sunlock
      ? DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET
      : DRIVER_SUNLOCK_STATE_INITIAL
    );
  };

  const _onManualSunlock = ({ raspberryPiSerialNumber }) => {
    onSunlockBarcodeText({ raspberryPiSerialNumber })
      .then((sunlock) => {
        return _onSunlock(sunlock);

      });
  };

  const _onSunlockBarcodeText = (barcode) => {
    onSunlockBarcodeText({ barcode })
      .then((sunlock) => {
        return _onSunlock(sunlock);
      });
  };

  return (
    <div styleName='card' className='fieldsContainer' id='field-sunlockId'>
      <span styleName='cardTitle'>
        Register Sunlock
      </span>

      <SrBox margin='16px 0 0'>
        {sunlockMissingError && viewState !== DRIVER_SUNLOCK_STATE_MANUAL_INPUT && (
          <SrBox padding='0 0 16px'>
            <SrErrorMessage>
              Sunlock not found. Please try again.
            </SrErrorMessage>
          </SrBox>
        )}

        {viewState === DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET && (
          <div styleName='sunlockBox'>
            <span styleName='title'>Serial Number</span>
            <br />
            <span styleName='serialNumber'>
              {sunlock?.raspberryPiSerialNumber}
            </span>
            <SrBox margin='16px 0 0'>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(DRIVER_SUNLOCK_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </div>
        )}
        {(
          viewState === DRIVER_SUNLOCK_STATE_INITIAL
          || viewState === DRIVER_SUNLOCK_STATE_BARCODE_SCANNER
        ) && (
          <>
            {!isUserAgentAndroidApp(userAgent) && (
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(DRIVER_SUNLOCK_STATE_BARCODE_SCANNER)}
              >
                Scan Barcode
              </ButtonDeprecated>
            )}
            <SrBox textAlign='center' padding='16px 0 0'>
              <SrText
                font='greycliff'
                weight='800'
                color='blue300'
                decoration='underline'
                size='16px'
                cursor='pointer'
                onClick={() => setViewState(DRIVER_SUNLOCK_STATE_MANUAL_INPUT)}
              >
                Manually input Serial Code
              </SrText>
            </SrBox>
          </>
        )}
        {viewState === DRIVER_SUNLOCK_STATE_MANUAL_INPUT && (
          <>
            <SrManualSunlockForm
              onSubmit={_onManualSunlock}
            />
            <SrBox textAlign='center' padding='16px 0 0'>
              <SrText
                font='greycliff'
                weight='800'
                color='blue300'
                decoration='underline'
                size='16px'
                cursor='pointer'
                onClick={() => setViewState(DRIVER_SUNLOCK_STATE_INITIAL)}
              >
                Scan Barcode
              </SrText>
            </SrBox>
          </>
        )}
      </SrBox>

      {viewState === DRIVER_SUNLOCK_STATE_BARCODE_SCANNER && (
        <SrBarcodeScanner
          onText={_onSunlockBarcodeText}
          onClose={() => setViewState(DRIVER_SUNLOCK_STATE_INITIAL)}
        />
      )}
      <SrFormikErrorMessage
        id='sunlockId'
        formikBag={formikBag}
      />
    </div>
  );
};

SrSunSetupDriverJobSunlockBarcode.propTypes = {
  sunlock: PropTypes.object,
  onSunlockBarcodeText: PropTypes.func.isRequired,
  formikBag: PropTypes.object.isRequired,
};

SrSunSetupDriverJobSunlockBarcode.displayName = 'SrSunSetupDriverJobSunlockBarcode';

export default SrSunSetupDriverJobSunlockBarcode;
